import { getActiveCarts } from "@/redux/reducers/checkout/thunk";
import { getAllCarts } from "@/redux/selectors";
import { StoreBrandingType } from "@/types/misc";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import { useTrackActions } from "@/utils/hooks/useTracking";
import {
  AnalyticsTrackingEvent,
  getEntityImage,
  getRetailerFromId,
} from "@brandclub/common-ui";
import { Box, styled, Typography } from "@mui/material";
import _isEqual from "lodash/isEqual";
import pluralize from "pluralize";
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { sumItemQuantities } from "../../utils/misc";
import {
  MOBILE_SCREEN_SIZE,
  TABLET_SCREEN_SIZE,
} from "../AppNavigation/constants";
import Loading from "../Loading";
import LandingPageWrapper from "../pages/AppPages/Pages/components/LandingPageWrapper";
import { UserLoginContext } from "../pages/Auth/UserLoginProvider";
import { OutlineButton, SolidButton } from "../StoreComponents/StoreButton";
import { CartProduct } from "./components/CartProduct";
import { useAsyncCheckoutEnabled } from "./components/CheckoutV2/hooks/useAsyncCheckoutEnabled";
import { EmptyBagMessageCard } from "./EmptyBagMessageCard";
import { UserRetailerShoppingCart } from "./types";
import { calculateTotal } from "./utils";

const StyledLandingPageWrapper = styled(LandingPageWrapper)({
  flex: 1,
  ".my-bag__wrapper": {
    flex: 1,
    paddingBottom: 105,
  },
});

const MyBagContainer = styled("div")({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  maxWidth: 735,
  padding: "0",
  alignItems: "center",
  gap: 40,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    gap: 30,
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    gap: 30,
  },
  ".head": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 15,
    maxWidth: 552,
    marginTop: 95,
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      alignSelf: "flex-start",
      marginTop: 48,
    },

    ".title": {
      fontSize: 24,
      fontWeight: 700,
      textAlign: "center",
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        fontSize: 22,
        textAlign: "left",
      },
    },
  },
  ".product_section": {
    width: "100%",
    display: "flex",

    flexDirection: "column",
    gap: 40,
    [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      gap: 30,
    },
    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      gap: 30,
    },
    ".retailer_cart": {},
    ".retailer": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 18,

      ".retailer_logo": {
        width: 30,
        height: 30,
        objectFit: "contain",
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          width: 25,
          height: 25,
        },
      },
      ".retailer_name": {
        flex: 1,
        fontSize: 18,
        fontWeight: 600,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 18,
          fontWeight: 600,
        },
      },
      ".actions": {},
    },
    ".group_info": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 20,
      marginBottom: 30,
      [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
        marginTop: 10,
        marginBottom: 24,
      },
      ".group_count": {
        fontSize: 16,
        fontWeight: 500,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 14,
        },
      },
      ".group_subtotal": {
        fontSize: 16,
        fontWeight: 500,
        [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
          fontSize: 14,
        },
        ".price": {
          fontWeight: 700,
        },
      },
    },

    ".product": {
      width: "100%",
    },
  },
});

const StyledEmptyBagMessageCard = styled(EmptyBagMessageCard)({
  margin: "auto",
  marginTop: 200,
  marginBottom: 200,
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    margin: "32px auto 0 auto",
  },
});

const RetailerCheckoutButton = styled(OutlineButton)(({ theme }) => ({
  transition: "all 0.5s",
  fontSize: 14,
  ":hover": {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 10,
    height: 27,
  },
}));
const OverAllCheckoutButton = styled(SolidButton)({
  fontSize: 16,
  height: 50,

  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
    fontSize: 14,
    height: 44,
    marginTop: 23,
  },
});

interface ProductSectionProps {
  items: UserRetailerShoppingCart[];
  onRetailerCheckout: (item: UserRetailerShoppingCart) => Promise<void>;
}
const ProductSection = (props: ProductSectionProps) => {
  return (
    <Box className="product_section">
      {/* Render all carts (those with > 0 items - filtered by redux already) */}
      {props.items.map((item, _index, allCarts) => (
        <Box
          key={`cartGroup:${item.shoppingCartId}`}
          display="flex"
          flexDirection="column"
          className="retailer_cart"
          gap={0}
        >
          <Box className="retailer">
            <img
              src={getEntityImage(item.retailerId, "retailer")}
              alt={`Logo for retailer: ${item.retailerId}`}
              className="retailer_logo"
            />
            <Typography className="retailer_name">
              {getRetailerFromId(item.retailerId)}
            </Typography>
            {allCarts.length >= 2 && (
              <RetailerCheckoutButton
                onClick={async () => {
                  await props.onRetailerCheckout(item);
                }}
              >
                Checkout {getRetailerFromId(item.retailerId)}
              </RetailerCheckoutButton>
            )}
          </Box>
          <Box className="group_info">
            <Box className="group_count">
              {pluralize("item", sumItemQuantities(item?.items), true)}
            </Box>
            <Box className="group_subtotal">
              Subtotal:{" "}
              <span className="price">{calculateTotal(item?.items ?? [])}</span>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "40px",
              [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
                gap: "30px",
              },
            }}
          >
            {(item?.items ?? []).map((product) => (
              <CartProduct
                large
                product={product}
                key={`cart:${item?.shoppingCartId}:item:${product.stacklineSku}`}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const MyBag = () => {
  const hasOptimisticItems = useAppSelector(
    (state) => !!state.checkout.optimisticAllCarts
  );
  const loadingCarts = useAppSelector((state) => state.checkout.loadingCarts);
  const storeDomainBrandingType = useAppSelector(
    (state) => state.appConfig?.domainConfig.storeBrandingType
  );
  const { send: userLoginSend } = useContext(UserLoginContext);
  const { signedIn } = useSignedIn();
  const allCarts = useAppSelector(getAllCarts, _isEqual);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [trackAction] = useTrackActions();

  const { enabled } = useAsyncCheckoutEnabled();

  useEffect(() => {
    !hasOptimisticItems && dispatch(getActiveCarts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = (retailerId: number) => {
    const retailerIds = allCarts.map((cart) => cart.retailerId);
    trackAction(
      AnalyticsTrackingEvent.BC_USER_REQUESTED_CHECKOUT,
      {
        cartItems: allCarts.map((cart) => ({
          items: cart.items,
          retailerId: cart.retailerId,
        })),
        retailerIds,
        retailerId: retailerIds[0],
        source: "mybag",
      },
      {
        sendImmediately: true,
      }
    );
    navigate(`/checkout?retailerId=${retailerId}`);
  };

  return (
    <StyledLandingPageWrapper wrapperClassName="my-bag__wrapper">
      {loadingCarts ? (
        <Loading dot></Loading>
      ) : (
        <MyBagContainer
          style={{ maxWidth: allCarts.length > 0 ? undefined : "100%" }}
        >
          {allCarts.length > 0 ? (
            <>
              <div className="head">
                <div className="title">Your Bag</div>
              </div>

              <ProductSection
                items={allCarts as any}
                onRetailerCheckout={async (selectedCart) => {
                  if (
                    !signedIn &&
                    storeDomainBrandingType !==
                      StoreBrandingType.CustomDTCStore &&
                    !enabled
                  ) {
                    userLoginSend({
                      type: "SIGN_IN",
                      redirectPath: { pathname: "/mybag" },
                    });
                  } else {
                    handleCheckout(selectedCart.retailerId);
                  }
                }}
              />

              <OverAllCheckoutButton
                sx={{
                  width: "100%",
                  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
                    width: "100%",
                    maxWidth: "100%",
                  },
                  [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
                    width: "100%",
                    maxWidth: "100%",
                  },
                }}
                onClick={() => {
                  handleCheckout(allCarts[0].retailerId);
                }}
                style={{ width: "100%" }}
              >
                {`Checkout${allCarts.length >= 2 ? " all retailers" : ""}`}
              </OverAllCheckoutButton>
            </>
          ) : (
            <StyledEmptyBagMessageCard />
          )}
        </MyBagContainer>
      )}
    </StyledLandingPageWrapper>
  );
};

export default MyBag;
